import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../../utils/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { Teacher } from '../../../utils/firebase';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import SelectButton from '../../../components/ui/SelectButton';
import SearchIcon from '../../../components/icons/SearchIcon';
import EditIcon from '../../../components/icons/EditIcon';
import TrashIcon from '../../../components/icons/TrashIcon';
import LeftArrowDoubleIcon from '../../../components/icons/LeftArrowDoubleIcon';
import RightArrowDoubleIcon from '../../../components/icons/RightArrowDoubleIcon';
import LeftArrowIcon from '../../../components/icons/LeftArrowIcon';
import RightArrowIcon from '../../../components/icons/RightArrowIcon';
import CheckCircleIcon from '../../../components/icons/CheckCircleIcon';
import { toast } from 'react-toastify';

interface ClassMaterial {
  id: string;
  name: string;
  type: 'PDF' | 'URL';
  sharePeriod: { start: string; end: string };
  uploadDate: string;
  status: 'sharing' | 'scheduled';
  fileUrl?: string;
  url?: string;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
}

const ClassMaterials: React.FC = () => {
  const { currentUser } = useAuth();
  const teacher = currentUser as Teacher;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState<'data' | 'mission'>('data');
  const [filter, setFilter] = useState<'all' | 'sharing' | 'scheduled'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [materials, setMaterials] = useState<ClassMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // 업로드/수정 다이얼로그 상태
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<ClassMaterial | null>(null);
  const [uploadType, setUploadType] = useState<'PDF' | 'URL'>('PDF');
  const [materialName, setMaterialName] = useState('');
  const [shareStartDate, setShareStartDate] = useState('');
  const [shareEndDate, setShareEndDate] = useState('');
  const [materialUrl, setMaterialUrl] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 날짜를 YYYY.MM.DD 형식으로 변환
  const formatDate = (date: Date | Timestamp | string): string => {
    let dateObj: Date;
    if (date instanceof Timestamp) {
      dateObj = date.toDate();
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else {
      dateObj = date;
    }
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  // 공유 기간에 따른 상태 계산
  const calculateStatus = (startDate: string, endDate: string): 'sharing' | 'scheduled' => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const start = new Date(startDate.replace(/\./g, '-'));
    start.setHours(0, 0, 0, 0);
    
    const end = new Date(endDate.replace(/\./g, '-'));
    end.setHours(23, 59, 59, 999);
    
    if (today >= start && today <= end) {
      return 'sharing';
    } else {
      return 'scheduled';
    }
  };

  // Firebase에서 자료 목록 가져오기
  useEffect(() => {
    if (!teacher?.schoolId || !teacher?.classId) {
      setLoading(false);
      return;
    }

    try {
      const materialsRef = collection(
        db,
        `schools/${teacher.schoolId}/classes/${teacher.classId}/classMaterials`
      );
      const q = query(materialsRef, orderBy('createdAt', 'desc'));

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const materialsData = snapshot.docs.map((docSnapshot) => {
            const data = docSnapshot.data();
            
            // sharePeriod 처리
            let startDate: string;
            let endDate: string;
            
            if (data.sharePeriod?.start) {
              if (data.sharePeriod.start instanceof Timestamp) {
                startDate = formatDate(data.sharePeriod.start.toDate());
              } else if (typeof data.sharePeriod.start === 'string') {
                // 이미 YYYY.MM.DD 형식이거나 YYYY-MM-DD 형식
                startDate = data.sharePeriod.start.replace(/-/g, '.');
              } else {
                startDate = formatDate(new Date(data.sharePeriod.start));
              }
            } else {
              startDate = data.createdAt ? formatDate(data.createdAt.toDate ? data.createdAt.toDate() : data.createdAt) : formatDate(new Date());
            }
            
            if (data.sharePeriod?.end) {
              if (data.sharePeriod.end instanceof Timestamp) {
                endDate = formatDate(data.sharePeriod.end.toDate());
              } else if (typeof data.sharePeriod.end === 'string') {
                endDate = data.sharePeriod.end.replace(/-/g, '.');
              } else {
                endDate = formatDate(new Date(data.sharePeriod.end));
              }
            } else {
              endDate = data.createdAt ? formatDate(data.createdAt.toDate ? data.createdAt.toDate() : data.createdAt) : formatDate(new Date());
            }
            
            const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date());
            const uploadDate = formatDate(createdAt);
            
            return {
              id: docSnapshot.id,
              name: data.name || '',
              type: data.type || 'URL',
              sharePeriod: {
                start: startDate,
                end: endDate
              },
              uploadDate: uploadDate,
              status: calculateStatus(startDate, endDate),
              fileUrl: data.fileUrl,
              url: data.url,
              createdAt: createdAt,
              updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : (data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date()),
              createdBy: data.createdBy
            } as ClassMaterial;
          });
          
          setMaterials(materialsData);
          setLoading(false);
        },
        (err) => {
          console.error('자료 목록 조회 중 오류:', err);
          setError('자료 목록을 불러오는 중 오류가 발생했습니다.');
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      console.error('자료 목록 조회 중 오류:', err);
      setError('자료 목록을 불러오는 중 오류가 발생했습니다.');
      setLoading(false);
    }
  }, [teacher?.schoolId, teacher?.classId]);

  // 필터링된 자료
  const filteredMaterials = materials.filter(material => {
    const matchesFilter = filter === 'all' || material.status === filter;
    const matchesSearch = material.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // 페이지네이션
  const totalPages = Math.ceil(filteredMaterials.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentMaterials = filteredMaterials.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  const formatDateRange = (start: string, end: string) => {
    return `${start}~${end}`;
  };

  // 자료 업로드 다이얼로그 열기
  const handleOpenUploadDialog = () => {
    setUploadType('PDF');
    setMaterialName('');
    setShareStartDate('');
    setShareEndDate('');
    setMaterialUrl('');
    setUploadFile(null);
    setIsUploadDialogOpen(true);
  };

  // 자료 수정 다이얼로그 열기
  const handleOpenEditDialog = (material: ClassMaterial) => {
    setEditingMaterial(material);
    setUploadType(material.type);
    setMaterialName(material.name);
    setShareStartDate(material.sharePeriod.start);
    setShareEndDate(material.sharePeriod.end);
    setMaterialUrl(material.url || material.fileUrl || '');
    setUploadFile(null);
    setIsEditDialogOpen(true);
  };

  // 자료 업로드
  const handleUpload = async () => {
    if (!teacher?.schoolId || !teacher?.classId || !teacher?.uid) {
      toast.error('사용자 정보를 찾을 수 없습니다.');
      return;
    }

    if (!materialName.trim()) {
      toast.error('자료명을 입력해주세요.');
      return;
    }

    if (!shareStartDate || !shareEndDate) {
      toast.error('공유 기간을 입력해주세요.');
      return;
    }

    if (uploadType === 'PDF' && !uploadFile && !editingMaterial) {
      toast.error('PDF 파일을 선택해주세요.');
      return;
    }

    if (uploadType === 'URL' && !materialUrl.trim()) {
      toast.error('URL을 입력해주세요.');
      return;
    }

    setUploading(true);
    try {
      let fileUrl = '';
      
      if (uploadType === 'PDF' && uploadFile) {
        // 파일 업로드
        const timestamp = Date.now();
        const fileName = `${timestamp}_${uploadFile.name}`;
        const storageRef = ref(
          storage,
          `schools/${teacher.schoolId}/classes/${teacher.classId}/classMaterials/${fileName}`
        );
        
        await uploadBytes(storageRef, uploadFile);
        fileUrl = await getDownloadURL(storageRef);
      } else if (uploadType === 'PDF' && editingMaterial?.fileUrl) {
        fileUrl = editingMaterial.fileUrl;
      }

      const materialData: any = {
        name: materialName.trim(),
        type: uploadType,
        sharePeriod: {
          start: shareStartDate.replace(/\./g, '-'),
          end: shareEndDate.replace(/\./g, '-')
        },
        createdAt: editingMaterial ? editingMaterial.createdAt : serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: teacher.uid
      };

      if (uploadType === 'PDF') {
        materialData.fileUrl = fileUrl;
      } else {
        materialData.url = materialUrl.trim();
      }

      if (editingMaterial) {
        // 수정
        const materialRef = doc(
          db,
          `schools/${teacher.schoolId}/classes/${teacher.classId}/classMaterials/${editingMaterial.id}`
        );
        await updateDoc(materialRef, materialData);
        toast.success('자료가 수정되었습니다.');
      } else {
        // 추가
        await addDoc(
          collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/classMaterials`),
          materialData
        );
        toast.success('자료가 업로드되었습니다.');
      }

      setIsUploadDialogOpen(false);
      setIsEditDialogOpen(false);
      setEditingMaterial(null);
      setMaterialName('');
      setShareStartDate('');
      setShareEndDate('');
      setMaterialUrl('');
      setUploadFile(null);
    } catch (err: any) {
      console.error('자료 업로드 중 오류:', err);
      toast.error('자료 업로드 중 오류가 발생했습니다.');
    } finally {
      setUploading(false);
    }
  };

  // 자료 삭제
  const handleDelete = async (material: ClassMaterial) => {
    if (!teacher?.schoolId || !teacher?.classId) {
      toast.error('사용자 정보를 찾을 수 없습니다.');
      return;
    }

    if (!window.confirm(`"${material.name}" 자료를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      // Firestore에서 삭제
      const materialRef = doc(
        db,
        `schools/${teacher.schoolId}/classes/${teacher.classId}/classMaterials/${material.id}`
      );
      await deleteDoc(materialRef);

      // Storage에서 파일 삭제 (PDF인 경우)
      if (material.type === 'PDF' && material.fileUrl) {
        try {
          const fileRef = ref(storage, material.fileUrl);
          await deleteObject(fileRef);
        } catch (storageErr) {
          console.warn('Storage 파일 삭제 중 오류 (무시됨):', storageErr);
        }
      }

      toast.success('자료가 삭제되었습니다.');
    } catch (err: any) {
      console.error('자료 삭제 중 오류:', err);
      toast.error('자료 삭제 중 오류가 발생했습니다.');
    }
  };

  // 파일 선택 핸들러
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('PDF 파일만 업로드 가능합니다.');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('파일 크기는 10MB를 초과할 수 없습니다.');
        return;
      }
      setUploadFile(file);
    }
  };

  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      padding: isMobile ? '16px' : '0',
      boxSizing: 'border-box',
      background: 'var(--Color-background-main, #F4F6FA)'
    }}>
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px'
      }}>
        {/* 제목 */}
        <div style={{
          color: 'var(--Color-text-normal, #161616)',
          fontSize: '32px',
          fontFamily: 'Pretendard',
          fontWeight: '600',
          lineHeight: '48px'
        }}>
          교육
        </div>

        {/* 탭 및 컨텐츠 영역 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          {/* 탭 버튼 */}
          <div style={{
            display: 'flex',
            gap: '16px'
          }}>
            <div
              style={{
                maxHeight: '40px',
                minHeight: '40px',
                paddingLeft: '12px',
                paddingRight: '12px',
                paddingTop: '8px',
                paddingBottom: '8px',
                background: activeTab === 'data' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
              onClick={() => setActiveTab('data')}
            >
              <div style={{
                color: activeTab === 'data' ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: '500',
                lineHeight: '21px'
              }}>
                학급 자료
              </div>
            </div>
            <div
              style={{
                maxHeight: '40px',
                minHeight: '40px',
                paddingLeft: '12px',
                paddingRight: '12px',
                paddingTop: '8px',
                paddingBottom: '8px',
                background: activeTab === 'mission' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
              onClick={() => setActiveTab('mission')}
            >
              <div style={{
                color: activeTab === 'mission' ? 'var(--Color-text-inverse, white)' : 'var(--Color-text-normal, #161616)',
                fontSize: '14px',
                fontFamily: 'Pretendard',
                fontWeight: '500',
                lineHeight: '21px'
              }}>
                데일리 미션
              </div>
            </div>
          </div>

          {/* 학급 자료 컨텐츠 */}
          {activeTab === 'data' && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {/* 통계 및 필터 영역 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }}>
                  {/* 좌측: 통계 및 필터 */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {/* 통계 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <div style={{
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '18px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '27px'
                      }}>
                        총
                      </div>
                      <div style={{
                        color: 'var(--Color-fill-brand-color, #FF6F0F)',
                        fontSize: '18px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '27px'
                      }}>
                        {filteredMaterials.length}
                      </div>
                      <div style={{
                        color: 'var(--Color-text-normal, #161616)',
                        fontSize: '18px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '27px'
                      }}>
                        개의 자료가 있습니다
                      </div>
                    </div>

                    {/* 필터 버튼 */}
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'center'
                    }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px',
                          cursor: 'pointer'
                        }}
                        onClick={() => setFilter('all')}
                      >
                        <CheckCircleIcon
                          width={16}
                          height={16}
                          color={filter === 'all' ? '#FF6F0F' : '#BABABA'}
                        />
                        <div style={{
                          color: filter === 'all' ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                          fontSize: '12px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '18px'
                        }}>
                          전체
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px',
                          cursor: 'pointer'
                        }}
                        onClick={() => setFilter('sharing')}
                      >
                        <CheckCircleIcon
                          width={16}
                          height={16}
                          color={filter === 'sharing' ? '#FF6F0F' : '#BABABA'}
                        />
                        <div style={{
                          color: filter === 'sharing' ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                          fontSize: '12px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '18px'
                        }}>
                          공유 중
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '2px',
                          cursor: 'pointer'
                        }}
                        onClick={() => setFilter('scheduled')}
                      >
                        <CheckCircleIcon
                          width={16}
                          height={16}
                          color={filter === 'scheduled' ? '#FF6F0F' : '#BABABA'}
                        />
                        <div style={{
                          color: filter === 'scheduled' ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                          fontSize: '12px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '18px'
                        }}>
                          공유 예정
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 우측: 자료 업로드 버튼 */}
                  <Button
                    variant="submit"
                    onClick={handleOpenUploadDialog}
                    width="118px"
                    height="40px"
                  >
                    자료 업로드
                  </Button>
                </div>
              </div>

              {/* 검색창 */}
              <div style={{
                width: '100%',
                height: '40px'
              }}>
                <div style={{
                  width: '100%',
                  height: '40px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  paddingTop: '8px',
                  paddingBottom: '8px',
                  background: 'white',
                  borderRadius: '999px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  border: '1px solid transparent'
                }}>
                  <SearchIcon
                    width={20}
                    height={20}
                    color="#222222"
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="자료 검색"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      border: 'none',
                      outline: 'none',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '400',
                      lineHeight: '19.5px',
                      color: searchQuery ? 'var(--Color-text-normal, #161616)' : 'var(--Color-text-assistive, #989898)',
                      background: 'transparent'
                    }}
                  />
                </div>
              </div>

              {/* 자료 테이블 */}
              <Card
                padding="16px 20px"
                background="white"
                borderRadius="16px"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                {/* 테이블 헤더 */}
                <div style={{
                  display: 'flex',
                  borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                  paddingBottom: '12px'
                }}>
                  <div style={{ width: '67px', padding: '0 16px', display: 'flex', alignItems: 'center' }}>
                    <div style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '19.5px'
                    }}>
                      No
                    </div>
                  </div>
                  <div style={{ width: '398px', padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                    <div style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '19.5px'
                    }}>
                      자료명
                    </div>
                  </div>
                  <div style={{ width: '421px', padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                    <div style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '19.5px'
                    }}>
                      공유 기간
                    </div>
                  </div>
                  <div style={{ width: '145px', padding: '0 8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '19.5px'
                    }}>
                      업로드 날짜
                    </div>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M4.93 8.93L6 10L8 8M6 10L8 12M6 10L8 8" stroke="#989898" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <div style={{ width: '108px', padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                    <div style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '19.5px'
                    }}>
                      상태
                    </div>
                  </div>
                  <div style={{ width: '65px', padding: '0 8px', display: 'flex', alignItems: 'center' }}>
                    <div style={{
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '19.5px'
                    }}>
                      관리
                    </div>
                  </div>
                </div>

                {/* 테이블 바디 */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  {loading ? (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard'
                    }}>
                      로딩 중...
                    </div>
                  ) : currentMaterials.length === 0 ? (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: 'var(--Color-text-assistive, #989898)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard'
                    }}>
                      등록된 자료가 없습니다.
                    </div>
                  ) : (
                    currentMaterials.map((material, index) => (
                    <div
                      key={material.id}
                      style={{
                        display: 'flex',
                        paddingTop: '16px',
                        paddingBottom: '16px',
                        paddingLeft: material.id === '3' ? '0' : undefined,
                        paddingRight: material.id === '3' ? '0' : undefined,
                        background: material.id === '3' ? 'var(--Color-fill-hover, #F5F5F5)' : 'transparent',
                        borderRadius: material.id === '3' ? '8px' : '0',
                        alignItems: 'center'
                      }}
                    >
                      {/* No */}
                      <div style={{
                        width: '68px',
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <div style={{
                          color: 'var(--Color-text-assistive, #989898)',
                          fontSize: '13px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '19.5px'
                        }}>
                          {startIndex + index + 1}
                        </div>
                      </div>

                      {/* 자료명 */}
                      <div style={{
                        width: '397px',
                        padding: '12px 4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px'
                      }}>
                        {/* 파일 타입 아이콘 */}
                        <div style={{
                          width: '44px',
                          height: '44px',
                          padding: '11.8px 9.66px',
                          background: 'var(--Color-background-sub, #F8F8F8)',
                          borderRadius: '4.29px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <div style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '12.88px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '19.32px'
                          }}>
                            {material.type}
                          </div>
                        </div>
                        {/* 파일 정보 */}
                        <div style={{
                          width: '286px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0'
                        }}>
                          <div style={{
                            color: 'var(--Color-text-normal, #161616)',
                            fontSize: '14px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '21px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {material.name}
                          </div>
                          <div style={{
                            color: 'var(--Color-text-assistive, #989898)',
                            fontSize: '12px',
                            fontFamily: 'Pretendard',
                            fontWeight: '400',
                            lineHeight: '18px'
                          }}>
                            {material.type} 파일
                          </div>
                        </div>
                      </div>

                      {/* 공유 기간 */}
                      <div style={{
                        width: '421px',
                        padding: '12px 8px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <div style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '13px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '19.5px'
                        }}>
                          {formatDateRange(material.sharePeriod.start, material.sharePeriod.end)}
                        </div>
                      </div>

                      {/* 업로드 날짜 */}
                      <div style={{
                        width: '144px',
                        padding: '12px 8px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <div style={{
                          color: 'var(--Color-text-normal, #161616)',
                          fontSize: '13px',
                          fontFamily: 'Pretendard',
                          fontWeight: '400',
                          lineHeight: '19.5px'
                        }}>
                          {material.uploadDate}
                        </div>
                      </div>

                      {/* 상태 */}
                      <div style={{
                        width: '108px',
                        height: '44px',
                        padding: '12px 0',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {material.status === 'sharing' ? (
                          <div style={{
                            padding: '2px 12px',
                            background: '#FFD2B5',
                            borderRadius: '8px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <div style={{
                              color: 'var(--Color-text-brand-color, #FF6F0F)',
                              fontSize: '12px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '18px'
                            }}>
                              공유중
                            </div>
                          </div>
                        ) : (
                          <div style={{
                            padding: '2px 12px',
                            background: 'white',
                            borderRadius: '8px',
                            border: '1px solid var(--Color-line-normal, #F0F0F0)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <div style={{
                              color: 'var(--Color-text-normal, #161616)',
                              fontSize: '12px',
                              fontFamily: 'Pretendard',
                              fontWeight: '500',
                              lineHeight: '18px'
                            }}>
                              공유 예정
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 관리 */}
                      <div style={{
                        width: '68px',
                        padding: '12px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleOpenEditDialog(material)}
                        >
                          <EditIcon
                            width={16}
                            height={16}
                            color="#989898"
                          />
                        </div>
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleDelete(material)}
                        >
                          <TrashIcon
                            width={16}
                            height={16}
                            color="#989898"
                          />
                        </div>
                      </div>
                    </div>
                    ))
                  )}
                </div>
              </Card>

              {/* 페이지네이션 */}
              {totalPages > 0 && (
                <div style={{
                  padding: '20px 0',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {/* 맨 처음 */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: currentPage === 1 ? 'default' : 'pointer'
                    }}
                    onClick={currentPage === 1 ? undefined : goToFirstPage}
                  >
                    <LeftArrowDoubleIcon
                      width={24}
                      height={24}
                      color={currentPage === 1 ? '#989898' : '#161616'}
                    />
                  </div>
                  {/* 이전 */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: currentPage === 1 ? 'default' : 'pointer'
                    }}
                    onClick={currentPage === 1 ? undefined : goToPreviousPage}
                  >
                    <LeftArrowIcon
                      width={24}
                      height={24}
                      color={currentPage === 1 ? '#989898' : '#161616'}
                    />
                  </div>
                  {/* 페이지 번호 */}
                  <div style={{
                    display: 'flex',
                    gap: '0'
                  }}>
                    {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage <= 2) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 1) {
                        pageNum = totalPages - 2 + i;
                      } else {
                        pageNum = currentPage - 1 + i;
                      }
                      return (
                        <div
                          key={pageNum}
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: currentPage === pageNum ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'transparent',
                            color: currentPage === pageNum ? 'white' : 'var(--Color-text-assistive, #989898)',
                            fontSize: '14px',
                            fontFamily: 'Pretendard',
                            fontWeight: '500',
                            lineHeight: '14px',
                            cursor: 'pointer'
                          }}
                          onClick={() => goToPage(pageNum)}
                        >
                          {pageNum}
                        </div>
                      );
                    })}
                  </div>
                  {/* 다음 */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: currentPage === totalPages ? 'default' : 'pointer'
                    }}
                    onClick={currentPage === totalPages ? undefined : goToNextPage}
                  >
                    <RightArrowIcon
                      width={24}
                      height={24}
                      color={currentPage === totalPages ? '#989898' : '#161616'}
                    />
                  </div>
                  {/* 맨 끝 */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: currentPage === totalPages ? 'default' : 'pointer'
                    }}
                    onClick={currentPage === totalPages ? undefined : goToLastPage}
                  >
                    <RightArrowDoubleIcon
                      width={24}
                      height={24}
                      color={currentPage === totalPages ? '#989898' : '#161616'}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 데일리 미션 컨텐츠 (추후 구현) */}
          {activeTab === 'mission' && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              color: 'var(--Color-text-assistive, #989898)',
              fontSize: '14px',
              fontFamily: 'Pretendard'
            }}>
              데일리 미션 기능은 추후 구현 예정입니다.
            </div>
          )}
        </div>
      </div>

      {/* 자료 업로드/수정 다이얼로그 */}
      {(isUploadDialogOpen || isEditDialogOpen) && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => {
            if (!uploading) {
              setIsUploadDialogOpen(false);
              setIsEditDialogOpen(false);
              setEditingMaterial(null);
            }
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
          >
            <Card
              padding="24px"
              background="white"
              borderRadius="16px"
              style={{
                width: '90%',
                maxWidth: '500px',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}
            >
            <div style={{
              fontSize: '20px',
              fontFamily: 'Pretendard',
              fontWeight: '600',
              color: 'var(--Color-text-normal, #161616)'
            }}>
              {editingMaterial ? '자료 수정' : '자료 업로드'}
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {/* 자료 타입 선택 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  color: 'var(--Color-text-normal, #161616)'
                }}>
                  자료 타입
                </div>
                <div style={{
                  display: 'flex',
                  gap: '12px'
                }}>
                  <div
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: `2px solid ${uploadType === 'PDF' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-line-normal, #F0F0F0)'}`,
                      backgroundColor: uploadType === 'PDF' ? '#FFF5F0' : 'white',
                      cursor: 'pointer',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      color: uploadType === 'PDF' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
                    }}
                    onClick={() => setUploadType('PDF')}
                  >
                    PDF 파일
                  </div>
                  <div
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: `2px solid ${uploadType === 'URL' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-line-normal, #F0F0F0)'}`,
                      backgroundColor: uploadType === 'URL' ? '#FFF5F0' : 'white',
                      cursor: 'pointer',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      color: uploadType === 'URL' ? 'var(--Color-fill-brand-color, #FF6F0F)' : 'var(--Color-text-normal, #161616)'
                    }}
                    onClick={() => setUploadType('URL')}
                  >
                    URL 링크
                  </div>
                </div>
              </div>

              {/* 자료명 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  color: 'var(--Color-text-normal, #161616)'
                }}>
                  자료명
                </div>
                <input
                  type="text"
                  value={materialName}
                  onChange={(e) => setMaterialName(e.target.value)}
                  placeholder="자료명을 입력하세요"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid var(--Color-line-normal, #F0F0F0)',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    outline: 'none'
                  }}
                />
              </div>

              {/* PDF 파일 업로드 또는 URL 입력 */}
              {uploadType === 'PDF' ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    color: 'var(--Color-text-normal, #161616)'
                  }}>
                    PDF 파일
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid var(--Color-line-normal, #F0F0F0)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard'
                    }}
                  />
                  {uploadFile && (
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--Color-text-assistive, #989898)',
                      fontFamily: 'Pretendard'
                    }}>
                      선택된 파일: {uploadFile.name}
                    </div>
                  )}
                  {editingMaterial?.fileUrl && !uploadFile && (
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--Color-text-assistive, #989898)',
                      fontFamily: 'Pretendard'
                    }}>
                      기존 파일이 유지됩니다. 새 파일을 선택하면 교체됩니다.
                    </div>
                  )}
                </div>
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    fontWeight: '500',
                    color: 'var(--Color-text-normal, #161616)'
                  }}>
                    URL 링크
                  </div>
                  <input
                    type="url"
                    value={materialUrl}
                    onChange={(e) => setMaterialUrl(e.target.value)}
                    placeholder="https://..."
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid var(--Color-line-normal, #F0F0F0)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      outline: 'none'
                    }}
                  />
                </div>
              )}

              {/* 공유 기간 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontFamily: 'Pretendard',
                  fontWeight: '500',
                  color: 'var(--Color-text-normal, #161616)'
                }}>
                  공유 기간
                </div>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center'
                }}>
                  <input
                    type="date"
                    value={shareStartDate.replace(/\./g, '-')}
                    onChange={(e) => {
                      const date = e.target.value.replace(/-/g, '.');
                      setShareStartDate(date);
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid var(--Color-line-normal, #F0F0F0)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      outline: 'none'
                    }}
                  />
                  <div style={{
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    color: 'var(--Color-text-assistive, #989898)'
                  }}>
                    ~
                  </div>
                  <input
                    type="date"
                    value={shareEndDate.replace(/\./g, '-')}
                    onChange={(e) => {
                      const date = e.target.value.replace(/-/g, '.');
                      setShareEndDate(date);
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid var(--Color-line-normal, #F0F0F0)',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* 버튼 */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <Button
                variant="cancel"
                onClick={() => {
                  if (!uploading) {
                    setIsUploadDialogOpen(false);
                    setIsEditDialogOpen(false);
                    setEditingMaterial(null);
                  }
                }}
                disabled={uploading}
              >
                취소
              </Button>
              <Button
                variant="submit"
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? '처리 중...' : (editingMaterial ? '수정' : '업로드')}
              </Button>
            </div>
          </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassMaterials;

